import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toPng } from "html-to-image";
import { CheckCircle2, Download, Film, Image as ImageIcon, Loader2, MessageCircle, MessagesSquare, Search, ShieldCheck } from "lucide-react";
import { Asset, CREATIVES, VIDEOS } from "./AdsKit";
import { buildCards } from "./answerCards";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_HOST = "myfeemitraapp.in";

function AnswerCard({ c, innerRef }) {
  return <div className={`ac${c.a.length > 5 ? " ac-dense" : ""}`} ref={innerRef}>
    <div className="ac-top"><span className="ac-brand"><ShieldCheck size={44} /> FeeMitra</span><span className="ac-icon"><c.icon size={56} /></span></div>
    <h3 className="ac-q">{c.q}</h3>
    <ul className="ac-a">{c.a.map(t => <li key={t}><CheckCircle2 size={40} /><span>{t}</span></li>)}</ul>
    <div className="ac-foot"><span>14 din FREE trial</span><b>{SITE_HOST}</b></div>
  </div>;
}

async function cardBlob(node) {
  const url = await toPng(node, { width: 1080, height: 1080, pixelRatio: 1, cacheBust: true });
  return (await fetch(url)).blob();
}

function CardTile({ c }) {
  const ref = useRef(null); const [busy, setBusy] = useState("");
  const name = `feemitra-${c.id}.png`;
  const download = async () => { setBusy("dl"); try { const b = await cardBlob(ref.current); const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = name; a.click(); } finally { setBusy(""); } };
  const share = async () => {
    setBusy("wa");
    try {
      const file = new File([await cardBlob(ref.current)], name, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) { await navigator.share({ files: [file], text: `${c.q} — ${SITE_HOST}` }).catch(() => {}); return; }
      const a = document.createElement("a"); a.href = URL.createObjectURL(file); a.download = name; a.click();
      window.open(`https://wa.me/?text=${encodeURIComponent(`${c.q}\n${c.a.map(t => `✅ ${t}`).join("\n")}\n\n14 din FREE trial: https://${SITE_HOST}/free-trial`)}`, "_blank");
    } finally { setBusy(""); }
  };
  return <figure className="ml-tile" data-testid={`ml-card-${c.id}`}>
    <div className="ml-thumb"><div className="ml-scale"><AnswerCard c={c} innerRef={ref} /></div></div>
    <figcaption><b>{c.q}</b>
      <div className="ml-btns">
        <button className="secondary-btn" onClick={download} disabled={!!busy} data-testid={`ml-download-${c.id}`}>{busy === "dl" ? <Loader2 size={14} className="spin" /> : <Download size={14} />} Download</button>
        <button className="primary-btn" onClick={share} disabled={!!busy} data-testid={`ml-share-${c.id}`}>{busy === "wa" ? <Loader2 size={14} className="spin" /> : <MessageCircle size={14} />} WhatsApp</button>
      </div></figcaption>
  </figure>;
}

export default function MediaLibrary() {
  const [plans, setPlans] = useState([]); const [biz, setBiz] = useState({}); const [q, setQ] = useState(""); const [tab, setTab] = useState("answers");
  useEffect(() => { axios.get(`${API}/public/plans`).then(r => setPlans(r.data)).catch(() => {}); axios.get(`${API}/public/business`).then(r => setBiz(r.data)).catch(() => {}); }, []);
  const cards = useMemo(() => buildCards(plans, biz), [plans, biz]);
  const s = q.trim().toLowerCase();
  const shown = cards.filter(c => !s || `${c.q} ${c.a.join(" ")}`.toLowerCase().includes(s));
  const tabs = [["answers", MessagesSquare, `Sawaal-jawaab (${cards.length})`], ["posters", ImageIcon, `Posters (${CREATIVES.length})`], ["videos", Film, `Videos (${VIDEOS.length})`]];
  return <div className="adk ml" data-testid="media-library-page">
    <div className="ml-bar">
      <div className="ml-tabs">{tabs.map(([k, I, t]) => <button key={k} className={tab === k ? "on" : ""} onClick={() => setTab(k)} data-testid={`ml-tab-${k}`}><I size={15} /> {t}</button>)}</div>
      {tab === "answers" && <label className="ld-search ml-search"><Search size={15} /><input value={q} onChange={e => setQ(e.target.value)} placeholder="Customer ka sawaal likhein… (e.g. receipt, price, login)" data-testid="ml-search-input" /></label>}
    </div>
    {tab === "answers" && <><p className="muted">Customer ke sawaal ke hisaab se image chunein → Download ya WhatsApp. Price aur support number live data se aate hain.</p>
      {shown.length ? <div className="ml-grid">{shown.map(c => <CardTile key={c.id} c={c} />)}</div> : <div className="empty-state" data-testid="ml-no-results">Koi sawaal nahi mila. Doosra shabd likhein.</div>}</>}
    {tab === "posters" && <div className="adk-grid">{CREATIVES.map(a => <Asset key={a.id} a={a} />)}</div>}
    {tab === "videos" && <div className="adk-grid">{VIDEOS.map(a => <Asset key={a.id} a={a} video />)}</div>}
  </div>;
}
